/**
 * Calculates horizontal position for the popover panel
 * @param {DOMRect} buttonRect - Bounding rectangle of the button element
 * @param {number} panelWidth - Width of the panel
 * @param {string} position - Position preference ('right' or 'left')
 * @returns {Object} Horizontal position properties
 */
const horizontalPosition = (buttonRect, panelWidth, position) => {
  if (position === 'right') {
    const hasSpaceRight = buttonRect.left + panelWidth <= window.innerWidth;
    if (hasSpaceRight) {
      // Position panel's left edge aligned with button's left edge
      return {
        left: `${buttonRect.left}px`,
        right: 'unset'
      };
    } else {
      // No space on right - align panel to fit within viewport (left edge)
      return {
        left: `${Math.max(0, window.innerWidth - panelWidth)}px`,
        right: 'unset'
      };
    }
  }

  if (position === 'center') {
    const buttonCenter = buttonRect.left + buttonRect.width / 2;

    return {
      left: `${buttonCenter}px`,
      right: 'unset',
      transform: 'translateX(-50%)'
    };
  }

  return {
    right:
      window.innerWidth - buttonRect.right + panelWidth > window.innerWidth
        ? `${window.innerWidth - panelWidth}px`
        : `${window.innerWidth - buttonRect.right}px`,
    left: 'unset'
  };
};

/**
 * Calculates the final position for the popover panel
 * @param {Object} params - Position calculation parameters
 * @param {DOMRect} params.buttonRect - Bounding rectangle of the button element
 * @param {DOMRect} params.panelRect - Bounding rectangle of the panel element
 * @param {number} params.panelWidth - Width of the panel
 * @param {string} params.position - Position preference ('right' or 'left')
 * @returns {Object} Complete position properties
 */
export const calculatePopoverPosition = ({ buttonRect, panelRect, panelWidth, position }) => {
  // Calculate space constraints. Vertical placement (above/below) and horizontal
  // placement are independent: horizontalPosition() always clamps the panel into
  // the viewport, so the above/below decision must NOT depend on horizontal space.
  const hasSpaceRight = buttonRect.right + panelWidth <= window.innerWidth;
  const hasSpaceLeft = buttonRect.left - panelWidth >= 0;
  const hasHorizontalSpace = hasSpaceRight || hasSpaceLeft;

  const hasSpaceBottom = buttonRect.bottom + panelRect.height <= window.innerHeight - 16;
  const hasSpaceTop = buttonRect.top - panelRect.height >= 0;

  // Case 1: No room above or below, but room beside - show centered beside the button
  if (!hasSpaceTop && !hasSpaceBottom && hasHorizontalSpace) {
    return {
      position: 'fixed',
      top: `${Math.max(0, (window.innerHeight - panelRect.height) / 2)}px`,
      left: hasSpaceRight ? `${buttonRect.right + 6}px` : `${buttonRect.left - panelWidth - 6}px`,
      right: 'unset'
    };
  }

  // Case 2: No room below but room above - show above (independent of horizontal space,
  // so a near-full-width panel on mobile still flips up instead of dropping below).
  if (!hasSpaceBottom && hasSpaceTop) {
    return {
      position: 'fixed',
      top: `${buttonRect.top - panelRect.height - 6}px`,
      ...horizontalPosition(buttonRect, panelWidth, position)
    };
  }

  // Case 3: No room above or below and no room beside - center on screen
  if (!hasSpaceTop && !hasSpaceBottom) {
    return {
      position: 'fixed',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)'
    };
  }

  // Case 4: Default - show below
  return {
    position: 'fixed',
    top: `${buttonRect.bottom + 6}px`,
    ...horizontalPosition(buttonRect, panelWidth, position)
  };
};
